import React, { useState } from 'react';
import { XCircle } from 'lucide-react';
import { useSession } from '../lib/SessionContext';
import { changeStatus, STATUS } from '../lib/appointments';
import { noticeHours, isCharged } from '../lib/cancellation';
import { toInputValue, fromInputValue } from '../lib/practiceTime';
import { t, formatDate, formatTime } from '../i18n';
import { Modal, Button, Field, Input, Alert } from './ui';

/**
 * Cancelling a session, as the professional records it (migration 0052).
 *
 * Who asked to cancel is asked every time, with nothing picked in advance: only
 * a patient's cancellation falls under the professional's cutoff, so a default
 * would either charge patients for the professional's own cancellations or
 * never charge anyone. When the patient gave notice starts at now and can be
 * moved back, for the patient who phoned yesterday.
 *
 * The dialog says whether it will be charged before anything is saved; the
 * database decides it again with the same rule.
 */

const CancelSessionModal = ({ appointment, patientName, onClose, onDone }) => {
  const { profile } = useSession();
  const [by, setBy] = useState(null);
  const [noticeAt, setNoticeAt] = useState(() => toInputValue(Date.now()));
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const policyHours = profile?.cancellation_notice_hours ?? null;
  const notice = by === 'patient' ? fromInputValue(noticeAt) : new Date();
  const charged = Boolean(by && notice && isCharged({
    policyHours, by, startsAt: appointment.starts_at, noticeAt: notice,
  }));
  // Any money that reached it, whole or part, moves on if it is not charged.
  const paid = ['paid', 'partial'].includes(appointment.patient_payment_status);

  let outcome = null;
  if (by === 'therapist') {
    outcome = t('cancellation.byYou');
  } else if (by === 'patient' && !policyHours) {
    outcome = t('cancellation.noPolicy');
  } else if (by === 'patient' && notice) {
    outcome = t(charged ? 'cancellation.charged' : 'cancellation.notCharged', {
      hours: Math.max(0, Math.floor(noticeHours(appointment.starts_at, notice))),
      policy: policyHours,
    });
  }

  const confirm = async () => {
    if (by === 'patient' && (!notice || notice.getTime() > Date.now() + 60000)) {
      setError(t('cancellation.noticeInvalid'));
      return;
    }
    setBusy(true);
    setError(null);
    const { error: cancelError } = await changeStatus({
      appointmentId: appointment.id,
      toStatus: STATUS.CANCELLED,
      reason: reason.trim() || null,
      cancelledBy: by,
      noticeAt: by === 'patient' ? notice.toISOString() : null,
    });
    setBusy(false);
    if (cancelError) { setError(cancelError.message); return; }
    onDone?.();
  };

  return (
    <Modal
      open
      title={t('appointments.cancelAppointment')}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>{t('common.back')}</Button>
          <Button variant="danger" onClick={confirm} loading={busy} disabled={!by}>
            <XCircle size={16} />{t('appointments.cancelAppointment')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <p className="text-sm font-medium text-slate-800">{patientName}</p>
          <p className="text-sm text-slate-600 first-letter:uppercase">
            {formatDate(appointment.starts_at, { weekday: 'long', day: 'numeric', month: 'long' })}
            {', '}{formatTime(appointment.starts_at)}
          </p>
        </div>
        <p className="text-sm text-slate-600">{t('agenda.cancelBody')}</p>

        {error && <Alert tone="danger">{error}</Alert>}

        <Field label={t('cancellation.who')} required>
          <div className="grid gap-2 sm:grid-cols-2">
            {[['patient', t('cancellation.byPatient')], ['therapist', t('cancellation.byTherapist')]].map(([value, label]) => (
              <label
                key={value}
                className={`flex cursor-pointer items-center gap-2 rounded-lg border p-3 text-sm transition ${
                  by === value ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <input type="radio" name="cancelled-by" checked={by === value} onChange={() => setBy(value)} />
                {label}
              </label>
            ))}
          </div>
        </Field>

        {by === 'patient' && (
          <Field label={t('cancellation.noticeAt')} hint={t('cancellation.noticeAtHint')}>
            <Input
              type="datetime-local"
              value={noticeAt}
              max={toInputValue(Date.now())}
              onChange={(event) => setNoticeAt(event.target.value)}
            />
          </Field>
        )}

        {outcome && (
          <Alert tone={charged ? 'warning' : 'info'}>
            {outcome}
            {paid && !charged && ` ${t('cancellation.paidMoves')}`}
          </Alert>
        )}

        <Field label={t('agenda.cancelReason')}>
          <Input
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder={t('agenda.cancelReasonPlaceholder')}
          />
        </Field>
      </div>
    </Modal>
  );
};

export default CancelSessionModal;
